import React, { useEffect, useState } from 'react';
import posthog from "posthog-js";
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Badge,
  Button,
  Spinner,
  Alert,
  Nav,
  NavItem,
  NavLink,
} from 'reactstrap';
import { FaRss, FaBell } from 'react-icons/fa';
import { getFeed, getAlerts } from '../actions/alerts';
import { AlertManagerPanel } from './AlertManager';
import { PatternSparkline } from './PatternSparkline';
import { buildDashboardUrl, buildPatternDetailUrl } from '../utils/links';
import { useAuthUrl } from '../utils/authRedirect';
import { useSeo } from "../hooks/useSeo";

function PriceSparkline({ prices, direction }: { prices: number[]; direction?: string }) {
  if (prices.length < 2) return null;
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  const range = max - min || 1;
  const w = 100, h = 40, pad = 3;
  const points = prices.map((p, i) => {
    const x = pad + (i / (prices.length - 1)) * (w - pad * 2);
    const y = pad + (1 - (p - min) / range) * (h - pad * 2);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');
  const color = direction === 'down' ? '#ef5350' : '#26a69a';
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} aria-hidden="true">
      <polyline points={points} fill="none" stroke={color} strokeWidth="1.8"
        strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

const SENTIMENT_COLORS: Record<string, string> = {
  bullish: 'success',
  bearish: 'danger',
  neutral: 'secondary',
};

function IndicatorBadges({ indicators }: { indicators?: PatternIndicatorSnapshot | null }) {
  if (!indicators) return null;
  const badges: React.ReactNode[] = [];

  if (indicators.rsi !== null && indicators.rsi !== undefined) {
    const rsi = indicators.rsi;
    const color = rsi < 30 ? 'info' : rsi > 70 ? 'warning' : 'secondary';
    badges.push(
      <Badge key="rsi" color={color} className="me-1">
        RSI {rsi.toFixed(1)}
      </Badge>
    );
  }

  if (indicators.macd !== null && indicators.macd !== undefined && indicators.macd.histogram !== null) {
    const hist = indicators.macd.histogram!
    badges.push(
      <Badge key="macd" color={hist >= 0 ? 'success' : 'danger'} className="me-1">
        MACD {hist >= 0 ? '▲' : '▼'}
      </Badge>
    );
  }

  if (indicators.bbands !== null && indicators.bbands !== undefined && indicators.bbands.upper !== null) {
    const { price, upper, lower } = indicators.bbands;
    const pos = price > upper ? 'Above BB' : price < lower! ? 'Below BB' : 'Inside BB';
    badges.push(
      <Badge key="bbands" color="secondary" className="me-1">
        {pos}
      </Badge>
    );
  }

  if (indicators.stoch !== null && indicators.stoch !== undefined && indicators.stoch.k !== null) {
    const k = indicators.stoch.k!
    const color = k < 20 ? 'info' : k > 80 ? 'warning' : 'secondary';
    badges.push(
      <Badge key="stoch" color={color} className="me-1">
        Stoch %K {k.toFixed(0)}
      </Badge>
    );
  }

  return <>{badges}</>;
}

function FeaturedSection({ featured }: { featured: FeaturedMatchData }) {
  const isDown = (featured.fraction_up ?? 100) < 50;
  const ratio = isDown ? featured.down_over_up : featured.up_over_down;
  const avgDir = isDown ? featured.avg_dn : featured.avg_up;
  const fracDir = isDown ? featured.fraction_dn : featured.fraction_up;
  const ratioColor = ratio !== null && ratio >= 2 ? 'success' : ratio !== null && ratio >= 1.5 ? 'warning' : 'secondary';
  return (
    <div className="mb-1 d-flex flex-wrap gap-2 align-items-center">
      <Badge color="info" className="me-1">
        {featured.window_name}
      </Badge>
      {ratio !== null && (
        <small className="text-muted">
          {isDown ? 'Down/Up' : 'Up/Down'}: <Badge color={ratioColor}>{ratio.toFixed(2)}x</Badge>
        </small>
      )}
      {avgDir !== null && (
        <small className="text-muted">
          {isDown ? 'Avg decline' : 'Avg return'}:{' '}
          <strong className={isDown ? 'text-danger' : 'text-success'}>
            {isDown ? '-' : '+'}{avgDir.toFixed(1)}%
          </strong>
        </small>
      )}
      {fracDir !== null && (
        <small className="text-muted">
          {isDown ? 'Bear rate' : 'Win rate'}: <strong>{fracDir.toFixed(0)}%</strong>
        </small>
      )}
      {featured.count_total !== null && (
        <small className="text-muted">
          Matches: <strong>{featured.count_total}</strong>
        </small>
      )}
    </div>
  );
}

function PatternSection({ pattern, indicators }: { pattern: PatternMatch; indicators?: PatternIndicatorSnapshot | null }) {
  const sentiment = pattern.sentiment;
  return (
    <div>
      <div className="mb-1">
        <Badge color={SENTIMENT_COLORS[sentiment || 'neutral']} className="me-2">
          {sentiment || 'neutral'}
        </Badge>
        <span className="text-muted small">
          {pattern.pattern_display_name || pattern.pattern_type.replace(/_/g, ' ')}
        </span>
        <small className="text-muted ms-2">
          Confidence: <strong>{(pattern.confidence * 100).toFixed(0)}%</strong>
        </small>
      </div>
      {indicators && (
        <div className="mb-1">
          <IndicatorBadges indicators={indicators} />
        </div>
      )}
    </div>
  );
}

function AlertBadges({ matched_alerts }: { matched_alerts: string[] }) {
  if (matched_alerts.length === 0) return null;
  return (
    <div className="mb-1">
      {matched_alerts.map((name) => (
        <Badge key={name} color="primary" pill className="me-1 small">
          {name}
        </Badge>
      ))}
    </div>
  );
}

// Maps window_name from SLICE_PARAMS to the query params the dashboard expects.
// Mirrors SliceParam definitions in daily.py.
const WINDOW_NAME_PARAMS: Record<string, { window: number; score: number; forecastWindow: number }> = {
  '2w':  { window: 10,  score: 99, forecastWindow: 1  },
  '4w':  { window: 20,  score: 93, forecastWindow: 3  },
  '3m':  { window: 65,  score: 80, forecastWindow: 5  },
  '6m':  { window: 130, score: 80, forecastWindow: 10 },
  '1y':  { window: 260, score: 80, forecastWindow: 20 },
};

function FeedCard({ item }: { item: FeedItem }) {
  const navigate = useNavigate();
  const { type, ticker, matched_alerts, featured, pattern, indicators } = item;

  const dashboardUrl = (() => {
    if (featured?.window_name && featured.date) {
      const params = WINDOW_NAME_PARAMS[featured.window_name];
      // betaAware=1 pins the beta-aware matcher so the live query reproduces this
      // beta-aware featured card (the nightly batch generates featured beta-aware too).
      return buildDashboardUrl({
        ticker, window: params.window, date: featured.date, score: params.score,
        numMatches: 100, forecastWindow: params.forecastWindow, betaAware: true,
      });
    }
    return buildDashboardUrl({ ticker, betaAware: true });
  })();
  const patternUrl = (() => {
    if (!pattern) return null;
    // PatternDetail's fetch defaults to lookback_days=1 server-side; pass a
    // lookback that reaches back to this pattern's detection date (+1 margin)
    // so the detail page can actually find it.
    const detectedMs = pattern.detected_date ? Date.parse(pattern.detected_date) : NaN;
    const daysAgo = Number.isFinite(detectedMs)
      ? Math.max(1, Math.ceil((Date.now() - detectedMs) / 86400000) + 1)
      : 30;
    return buildPatternDetailUrl({ ticker, patternType: pattern.pattern_type, startDate: pattern.start_date, window: pattern.window, lookbackDays: daysAgo });
  })();

  const dateLabel = (() => {
    if (featured?.date) return featured.date;
    if (pattern?.end_date) return pattern.end_date;
    return '';
  })();

  const handleCardClick = () => {
    posthog.capture("feed_item_clicked", { ticker, type });
    navigate(dashboardUrl);
  };

  return (
    <Card
      className="mb-3 pattern-card"
      style={{ cursor: 'pointer' }}
      onClick={handleCardClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter') handleCardClick(); }}
    >
      <CardBody>
        <Row className="align-items-center">
          <Col xs="auto" onClick={(e) => e.stopPropagation()}>
            <div className="fw-bold fs-5">
              <Link to={dashboardUrl} className="text-decoration-none">
                {ticker}
              </Link>
            </div>
            {dateLabel && <small className="text-muted">{dateLabel}</small>}
          </Col>
          <Col xs="auto" className="px-2">
            {pattern?.pattern_type ? (
              <PatternSparkline patternType={pattern.pattern_type} width={100} height={40} />
            ) : featured?.query_data_norm && featured.query_data_norm.length > 1 ? (
              <PriceSparkline prices={featured.query_data_norm} direction={(featured.fraction_up ?? 100) >= 50 ? 'up' : 'down'} />
            ) : null}
          </Col>
          <Col>
            {type === 'featured_match' && featured && (
              <FeaturedSection featured={featured} />
            )}
            {type === 'classic_pattern' && pattern && (
              <PatternSection pattern={pattern} indicators={indicators} />
            )}
            {type === 'combined' && (
              <>
                {featured && (
                  <div className="mb-2 pb-2 border-bottom">
                    <small className="text-uppercase text-muted fw-semibold d-block mb-1" style={{ fontSize: '0.65rem', letterSpacing: '0.05em' }}>
                      Similarity Matcher
                    </small>
                    <FeaturedSection featured={featured} />
                  </div>
                )}
                {pattern && (
                  <div>
                    <small className="text-uppercase text-muted fw-semibold d-block mb-1" style={{ fontSize: '0.65rem', letterSpacing: '0.05em' }}>
                      Chart Pattern
                    </small>
                    <PatternSection pattern={pattern} indicators={indicators} />
                  </div>
                )}
              </>
            )}
          </Col>
          <Col xs="auto" className="text-end" onClick={(e) => e.stopPropagation()}>
            <AlertBadges matched_alerts={matched_alerts} />
            <div className="d-flex gap-1 justify-content-end">
              <Link to={dashboardUrl} onClick={() => posthog.capture("feed_item_clicked", { ticker, type })}>
                <Button size="sm" color="outline-secondary">
                  Chart →
                </Button>
              </Link>
              {patternUrl && type !== 'featured_match' && (
                <Link to={patternUrl}>
                  <Button size="sm" color="outline-secondary">
                    Pattern →
                  </Button>
                </Link>
              )}
            </div>
          </Col>
        </Row>
      </CardBody>
    </Card>
  );
}

function itemDateKey(item: FeedItem): string {
  if (item.featured?.date) return item.featured.date;
  if (item.pattern?.detected_date) return item.pattern.detected_date;
  if (item.pattern?.end_date) return item.pattern.end_date;
  return '';
}

function itemKey(item: FeedItem, i: number): string {
  const pat = item.pattern ? `${item.pattern.pattern_type}-${item.pattern.start_date}` : 'fm';
  return `${item.ticker}-${pat}-${i}`;
}

function groupByDate(items: FeedItem[]): Record<string, FeedItem[]> {
  const groups: Record<string, FeedItem[]> = {};
  for (const item of items) {
    const date = itemDateKey(item);
    (groups[date] ??= []).push(item);
  }
  return groups;
}

function FeedTab({ onGoToAlerts }: { onGoToAlerts: () => void }) {
  const dispatch = useDispatch();
  const alertsState = useSelector((state: RootReducerState) => state.alerts);
  const [lookbackDays, setLookbackDays] = useState(7);

  useEffect(() => {
    dispatch(getAlerts() as never);
    dispatch(getFeed(lookbackDays) as never);
  }, [dispatch, lookbackDays]);

  const { feed, feedLoading, alerts, error } = alertsState;
  const groups = groupByDate(feed);
  const sortedDates = Object.keys(groups).sort((a, b) => b.localeCompare(a));

  return (
    <>
      <Row className="mb-3 align-items-center">
        <Col>
          {alerts.length > 0 && (
            <small className="text-muted">
              {alerts.filter((a) => a.active).length} active alert
              {alerts.filter((a) => a.active).length !== 1 ? 's' : ''}
            </small>
          )}
        </Col>
        <Col xs="auto" className="d-flex gap-2 align-items-center">
          <small className="text-muted">Last:</small>
          {[1, 7, 14].map((days) => (
            <Button
              key={days}
              size="sm"
              color={lookbackDays === days ? 'primary' : 'outline-secondary'}
              onClick={() => setLookbackDays(days)}
            >
              {days === 1 ? 'Today' : `${days}d`}
            </Button>
          ))}
        </Col>
      </Row>

      {error && <Alert color="danger">{error}</Alert>}

      {feedLoading && (
        <div className="text-center mt-5 mb-5">
          <Spinner />
        </div>
      )}

      {!feedLoading && alerts.length === 0 && (
        <div className="text-center mt-5 mb-5">
          <p className="text-muted">No alerts configured yet.</p>
          <p className="text-muted small mb-3">Create an alert to get notified when your criteria are met.</p>
          <Button color="primary" onClick={onGoToAlerts}>
            Set Up an Alert →
          </Button>
        </div>
      )}

      {!feedLoading && alerts.length > 0 && feed.length === 0 && (
        <div className="text-center mt-5">
          <p className="text-muted">
            No patterns matched your alerts in the last {lookbackDays} day
            {lookbackDays !== 1 ? 's' : ''}.
          </p>
          <p className="text-muted small">
            Try broadening your filters or extending the lookback window.
          </p>
        </div>
      )}

      {!feedLoading &&
        sortedDates.map((date) => (
          <div key={date} className="mb-4">
            <h6 className="text-muted text-uppercase mb-2 border-bottom pb-1">
              {date}
            </h6>
            {groups[date].map((item, i) => (
              <FeedCard key={itemKey(item, i)} item={item} />
            ))}
          </div>
        ))}
    </>
  );
}

export function Feed() {
  useSeo({ title: "Your Feed", path: "/feed", noindex: true });
  const authUrlFor = useAuthUrl();
  const [searchParams, setSearchParams] = useSearchParams();
  const loggedIn = useSelector((state: RootReducerState) => state.authentication.loggedIn);
  const authChecked = useSelector((state: RootReducerState) => state.authentication.authChecked);
  const user = useSelector((state: RootReducerState) => state.authentication.user);
  const isPro = ((user as { groups?: string[] } | null)?.groups ?? []).includes('pro');

  const [autoOpenAlerts, setAutoOpenAlerts] = useState(false);
  const activeTab = searchParams.get('tab') === 'alerts' ? 'alerts' : 'feed';
  const setTab = (tab: string, autoOpen = false) => {
    setAutoOpenAlerts(autoOpen);
    setSearchParams(tab === 'feed' ? {} : { tab });
    posthog.capture("feed_tab_switched", { tab });
  };

  // Wait for the initial Cognito session check before deciding — otherwise a direct
  // load/refresh briefly flashes the sign-in prompt before auth hydrates.
  if (!authChecked) {
    return (
      <Container className="mt-5 text-center">
        <Spinner /> <span className="ms-2">Loading…</span>
      </Container>
    );
  }

  if (!loggedIn) {
    return (
      <Container className="mt-5 text-center">
        <h4>Sign in to view your personalized feed</h4>
        <Link to={authUrlFor("/login")}>
          <Button color="primary" className="mt-3">Log In</Button>
        </Link>
      </Container>
    );
  }

  // Feed + Alerts are Pro-only (the API returns 403 for free tier). Gate here so
  // free users get an upgrade prompt instead of failing API calls / a broken page.
  if (!isPro) {
    posthog.capture("upgrade_prompt_seen", { location: "feed" });
    return (
      <Container className="mt-5 text-center">
        <h4>Your personalized feed is a Pro feature</h4>
        <p className="text-muted">
          Upgrade to Pro to create alerts and get a custom feed of matches against your criteria.
        </p>
        <Link to="/pricing">
          <Button color="primary" className="mt-2">Upgrade to Pro →</Button>
        </Link>
      </Container>
    );
  }

  return (
    <Container className="mt-4">
      <Row className="mb-3 align-items-center">
        <Col>
          <h4 className="mb-0">My Feed</h4>
        </Col>
      </Row>

      <Nav tabs className="mb-4">
        <NavItem>
          <NavLink
            href="#"
            active={activeTab === 'feed'}
            onClick={(e) => { e.preventDefault(); setTab('feed'); }}
          >
            <FaRss className="me-2" />Feed
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink
            href="#"
            active={activeTab === 'alerts'}
            onClick={(e) => { e.preventDefault(); setTab('alerts', false); }}
          >
            <FaBell className="me-2" />Alerts
          </NavLink>
        </NavItem>
      </Nav>

      {activeTab === 'feed' && <FeedTab onGoToAlerts={() => setTab('alerts', true)} />}
      {activeTab === 'alerts' && <AlertManagerPanel autoOpen={autoOpenAlerts} />}
    </Container>
  );
}
